'use client'

import { useState, useEffect, useCallback } from 'react'
import { useAI } from '@/context/AIContext'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { Button } from '@/components/ui/button'
import { agentApi, type ConversationSummary } from '@/lib/agent-api'

// ============================================================================
// Thread List Pane Component
// ============================================================================

export function ThreadListPane() {
  const {
    currentConversationId,
    conversationContext,
    clearHistory,
    loadConversation,
  } = useAI()

  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [filterType, setFilterType] = useState<string | undefined>(undefined)
  const [loadingConversationId, setLoadingConversationId] = useState<string | null>(null)
  const [deletingConversationId, setDeletingConversationId] = useState<string | null>(null)

  const loadConversations = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await agentApi.listConversations(filterType)
      setConversations(response.conversations)
    } catch (error) {
      console.error('Failed to load conversations:', error)
    } finally {
      setIsLoading(false)
    }
  }, [filterType])

  // Load conversations
  useEffect(() => {
    loadConversations()
  }, [loadConversations])

  // Handle new chat
  const handleNewChat = () => {
    clearHistory()
  }

  // Handle conversation click
  const handleConversationClick = async (conversationId: string) => {
    console.log('[ThreadListPane] Conversation clicked:', conversationId)
    
    if (conversationId === currentConversationId) {
      console.log('[ThreadListPane] Already loaded, skipping')
      return // Already loaded
    }

    setLoadingConversationId(conversationId)
    try {
      console.log('[ThreadListPane] Loading conversation...')
      await loadConversation(conversationId)
      console.log('[ThreadListPane] Conversation loaded successfully')
    } catch (error) {
      console.error('[ThreadListPane] Failed to load conversation:', error)
    } finally {
      setLoadingConversationId(null)
    }
  }

  // Handle conversation delete
  const handleDeleteConversation = async (conversationId: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (!confirm('Are you sure you want to delete this conversation? This action cannot be undone.')) {
      return
    }

    setDeletingConversationId(conversationId)
    try {
      await agentApi.deleteConversation(conversationId)
      
      // If the deleted conversation was the current one, clear it
      if (conversationId === currentConversationId) {
        clearHistory()
      }
      
      // Reload conversations list
      await loadConversations()
      
      console.log('[ThreadListPane] Conversation deleted successfully')
    } catch (error) {
      console.error('[ThreadListPane] Failed to delete conversation:', error)
      alert('Failed to delete conversation. Please try again.')
    } finally {
      setDeletingConversationId(null)
    }
  }

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60)

    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    } else if (diffInHours < 168) {
      return date.toLocaleDateString('en-US', { weekday: 'short' })
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }
  }

  // Get context icon
  const getContextIcon = (contextType: string) => {
    switch (contextType) {
      case 'incident':
        return <Icons.alertCircle className="h-4 w-4 text-red-500" />
      case 'policy':
        return <Icons.shield className="h-4 w-4 text-blue-500" />
      default:
        return <Icons.messageSquare className="h-4 w-4 text-gray-500" />
    }
  }

  return (
    <div className="flex flex-col h-full bg-gray-50/50">
      {/* Header */}
      <div className="p-4 border-b border-border/30 bg-white">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-brand-navy">
            Conversations
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleNewChat}
            className="gap-1.5"
          >
            <Icons.plus className="h-4 w-4" />
            <span>New</span>
          </Button>
        </div>

        {/* Context Scope Badge */}
        {conversationContext && (
          <div className={cn(
            'inline-flex items-center gap-2 rounded-full px-3 py-1.5',
            'bg-gradient-to-r from-brand-cornflower/10 to-brand-purple/10',
            'text-brand-navy border border-brand-cornflower/20',
            'text-xs font-medium'
          )}>
            {getContextIcon(conversationContext.type || 'general')}
            <span>
              {conversationContext.type === 'incident' && 'Incident Context'}
              {conversationContext.type === 'policy' && 'Policy Context'}
              {conversationContext.type === 'general' && 'General Chat'}
            </span>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-1 mt-3">
          <button
            onClick={() => setFilterType(undefined)}
            className={cn(
              'px-3 py-1 rounded-md text-xs font-medium transition-colors',
              filterType === undefined
                ? 'bg-brand-navy text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            )}
          >
            All
          </button>
          <button
            onClick={() => setFilterType('incident')}
            className={cn(
              'px-3 py-1 rounded-md text-xs font-medium transition-colors',
              filterType === 'incident'
                ? 'bg-brand-navy text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            )}
          >
            Incidents
          </button>
          <button
            onClick={() => setFilterType('general')}
            className={cn(
              'px-3 py-1 rounded-md text-xs font-medium transition-colors',
              filterType === 'general'
                ? 'bg-brand-navy text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            )}
          >
            General
          </button>
        </div>
      </div>

      {/* Thread List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin h-6 w-6 border-2 border-brand-cornflower border-t-transparent rounded-full" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <Icons.messageSquare className="h-12 w-12 text-gray-300 mb-3" />
            <p className="text-sm text-gray-500">No conversations yet</p>
            <p className="text-xs text-gray-400 mt-1">Start chatting to create a thread</p>
          </div>
        ) : (
          <div className="divide-y divide-border/20">
            {conversations.map((conv) => (
              <div
                key={conv.id}
                className={cn(
                  'relative group hover:bg-white transition-colors',
                  currentConversationId === conv.id && 'bg-white border-l-2 border-brand-cornflower'
                )}
              >
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    console.log('[ThreadListPane] Button clicked for conversation:', conv.id)
                    handleConversationClick(conv.id)
                  }}
                  disabled={loadingConversationId === conv.id || deletingConversationId === conv.id}
                  className={cn(
                    'w-full px-4 py-3 text-left cursor-pointer',
                    (loadingConversationId === conv.id || deletingConversationId === conv.id) && 'opacity-50 cursor-wait',
                    'z-10'
                  )}
                  style={{ pointerEvents: 'auto' }}
                >
                  <div className="flex items-start gap-2">
                    {loadingConversationId === conv.id ? (
                      <div className="animate-spin h-4 w-4 border-2 border-brand-cornflower border-t-transparent rounded-full" />
                    ) : (
                      getContextIcon(conv.context_type)
                    )}
                    <div className="flex-1 min-w-0 pr-8">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {conv.title}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500" suppressHydrationWarning>
                          {formatTimestamp(conv.last_message_at)}
                        </span>
                        {conv.message_count && (
                          <span className="text-xs text-gray-400">
                            {conv.message_count} messages
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
                
                {/* Delete button - shown on hover */}
                <button
                  onClick={(e) => handleDeleteConversation(conv.id, e)}
                  disabled={deletingConversationId === conv.id}
                  className={cn(
                    'absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md',
                    'opacity-0 group-hover:opacity-100 transition-opacity',
                    'hover:bg-red-50 hover:text-red-600',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    'z-20'
                  )}
                  style={{ pointerEvents: 'auto' }}
                  title="Delete conversation"
                >
                  {deletingConversationId === conv.id ? (
                    <div className="animate-spin h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full" />
                  ) : (
                    <Icons.trash className="h-4 w-4" />
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
